import { api } from '../client';

// Types for API responses
export interface ApiResponse<T = any> {
  success: boolean;
  message: string;
  data: T;
}

export interface PaginationInfo {
  current_page: number;
  last_page: number;
  per_page: number;
  from: number;
  to: number;
  total: number;
  first_page_url: string;
  last_page_url: string;
  next_page_url: string | null;
  prev_page_url: string | null;
  path: string;
}

export interface PaginatedResponse<T> {
  current_page: number;
  data: T[];
  first_page_url: string;
  from: number;
  last_page: number;
  last_page_url: string;
  next_page_url: string | null;
  path: string;
  per_page: number;
  prev_page_url: string | null;
  to: number;
  total: number;
}

export interface Category {
  uuid: string;
  name: string;
  description: string;
  is_active: boolean;
  sort_order: number;
}

// Types for Employer Charity Management
export interface Charity {
  uuid: string;
  category_uuid: string;
  category: Category;
  name: string;
  abn: string;
  contact_email: string;
  contact_phone: string;
  website: string | null;
  head_office_location: string;
  charity_government_id: string;
  verified: number;
  verified_bsb_no: string;
  verified_acc_no: string;
  verified_acc_name: string;
}

export interface Employer {
  uuid: string;
  name: string;
  abn: string;
  contact_name: string;
  contact_email: string;
  contact_phone: string;
  head_office_location: string;
}

export interface EmployerCharity {
  uuid: string;
  charity: Charity;
  employer: Employer;
}

export interface EmployerCharityRequest {
  employer_uuid: string;
  charity_uuid: string;
}

export interface EmployerCharityListParams {
  page?: number;
  per_page?: number;
  all?: boolean;
  column?: 'created_at' | 'updated_at';
  order?: 'asc' | 'desc';
  phrase?: string;
  start_date?: string; // YYYY-MM-DD
  end_date?: string; // YYYY-MM-DD
  sql?: boolean;
}

// Charity View Types
export interface DonationItem {
    donation_request_uuid: string;
    user_uuid: string;
    employee_name: string;
    employer_uuid: string;
    employer_name: string;
    leave_hours: number;
    leave_value: number;
    employer_top_up: number;
    total_value: number;
    status: string;
    created_at: string;
    approved_at: string | null;
    employer_comment: string | null;
}

export interface PaymentItem {
    payout_uuid: string;
    charity_uuid: string;
    charity_name: string;
    amount: number;
    transaction_date: string;
    status: string;
    reference_number: string;
    notes: string | null;
}

export interface CharityViewResponse {
    donations: {
        items: DonationItem[];
        paging: PaginationInfo;
    };
    payments: {
        items: PaymentItem[];
        paging: PaginationInfo;
    };
}

// Filter Types
export interface DonationFilters {
    donation_status?: string;
    donation_date_from?: string;
    donation_date_to?: string;
    donation_phrase?: string;
    donation_sort_by?: string;
    donation_sort_direction?: 'asc' | 'desc';
    donation_page?: number;
    donation_per_page?: number;
}

export interface PaymentFilters {
    payment_status?: string;
    payment_date_from?: string;
    payment_date_to?: string;
    payment_phrase?: string;
    payment_sort_by?: string;
    payment_sort_direction?: 'asc' | 'desc';
    payment_page?: number;
    payment_per_page?: number;
}

export interface CharityViewFilters extends DonationFilters, PaymentFilters {}

// API base URL for employer charity
const API_BASE_URL = '/employer-charity';

export class EmployerCharityService {
  private static instance: EmployerCharityService;

  private constructor() {}

  public static getInstance(): EmployerCharityService {
    if (!EmployerCharityService.instance) {
      EmployerCharityService.instance = new EmployerCharityService();
    }
    return EmployerCharityService.instance;
  }

  /**
   * Get a list of employer-charity relationships
   */
  public async getList(params?: EmployerCharityListParams): Promise<ApiResponse<PaginatedResponse<EmployerCharity>>> {
    try {
      const response = await api.get<ApiResponse<PaginatedResponse<EmployerCharity>>>(API_BASE_URL, { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching employer charities:', error);
      throw error;
    }
  }

  /**
   * Get a specific employer-charity relationship
   */
  public async getById(uuid: string): Promise<ApiResponse<EmployerCharity>> {
    try {
      const response = await api.get<ApiResponse<EmployerCharity>>(`${API_BASE_URL}/${uuid}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching employer charity with UUID ${uuid}:`, error);
      throw error;
    }
  }

  /**
   * Get charity details
   */
  public async getCharity(uuid: string): Promise<ApiResponse<Charity>> {
    try {
      const response = await api.get<ApiResponse<Charity>>(`/charity/${uuid}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching charity details with UUID ${uuid}:`, error);
      throw error;
    }
  }

  /**
   * Create a new employer-charity relationship
   */
  public async create(data: EmployerCharityRequest): Promise<ApiResponse<EmployerCharity>> {
    try {
      const response = await api.post<ApiResponse<EmployerCharity>>(API_BASE_URL, data);
      return response.data;
    } catch (error) {
      console.error('Error creating employer charity relationship:', error);
      throw error;
    }
  }

  /**
   * Update an existing employer-charity relationship
   */
  public async update(uuid: string, data: EmployerCharityRequest): Promise<ApiResponse<EmployerCharity>> {
    try {
      const response = await api.put<ApiResponse<EmployerCharity>>(`${API_BASE_URL}/${uuid}`, data);
      return response.data;
    } catch (error) {
      console.error(`Error updating employer charity with UUID ${uuid}:`, error);
      throw error;
    }
  }

  /**
   * Delete an employer-charity relationship
   */
  public async delete(uuid: string): Promise<ApiResponse<any>> {
    try {
      const response = await api.delete<ApiResponse<any>>(`${API_BASE_URL}/${uuid}`);
      return response.data;
    } catch (error) {
      console.error(`Error deleting employer charity with UUID ${uuid}:`, error);
      throw error;
    }
  }

  /**
   * Get charity view data including donations and payments
   */
  public async getCharityView(
        charityUuid: string,
        filters?: CharityViewFilters
    ): Promise<ApiResponse<CharityViewResponse>> {
        try {
            // Remove empty string values from filters to avoid validation issues
            const cleanFilters: any = {};
            if (filters) {
                Object.keys(filters).forEach(key => {
                    const value = filters[key as keyof CharityViewFilters];
                    // Only include non-empty values (allow 0, false, but exclude empty strings)
                    if (value !== '' && value !== null && value !== undefined) {
                        cleanFilters[key] = value;
                    }
                });
            }
            
            const response = await api.get<ApiResponse<CharityViewResponse>>(
                `/employer-charity/view/${charityUuid}`,
                { params: cleanFilters }
            );
            return response.data;
        } catch (error) {
            console.error(`Error fetching charity view for UUID ${charityUuid}:`, error);
      throw error;
    }
  }
}

export default EmployerCharityService.getInstance(); 